import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PublicLayout } from '../components/PublicLayout';
import { Search, MapPin, Building2, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIVISIONS = ['Semua', 'Kuching', 'Samarahan', 'Sri Aman', 'Betong', 'Sarikei', 'Sibu', 'Mukah', 'Kapit', 'Bintulu', 'Miri', 'Limbang', 'Serian'];

export default function DirektoriPerguruan() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [division, setDivision] = useState('Semua');

  useEffect(() => {
    axios.get(`${API_URL}/api/schools/public`)
      .then(r => setSchools(r.data || []))
      .catch(() => setSchools([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = schools.filter(s => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.district?.toLowerCase().includes(search.toLowerCase());
    const matchDiv = division === 'Semua' || s.division === division;
    return matchSearch && matchDiv;
  });

  return (
    <PublicLayout>
      <div className="bg-[#0B0B0B] text-white py-14">
        <div className="h-0.5 bg-gradient-to-r from-[#D71920] via-[#FFD21F] to-[#D71920] -mt-14 mb-14" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs text-[#FFD21F] font-bold tracking-widest uppercase mb-2">Utama / Direktori</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold" data-testid="direktori-title">Direktori Perguruan</h1>
          <p className="text-[#999] mt-2 text-sm max-w-lg">
            Senarai perguruan silat yang telah disahkan dan berdaftar secara rasmi dengan Rumpun Silat Sarawak.
          </p>
        </div>
      </div>

      {/* Filters */}
      <section className="bg-white py-8 border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#bbb]" />
              <input
                type="text"
                placeholder="Cari nama perguruan atau daerah..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:border-[#0B0B0B]"
                data-testid="search-input"
              />
            </div>
            {/* Division filter */}
            <select
              value={division}
              onChange={e => setDivision(e.target.value)}
              className="py-2.5 px-3 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:border-[#0B0B0B] bg-white text-[#555]"
              data-testid="division-filter"
            >
              {DIVISIONS.map(d => (
                <option key={d} value={d}>{d === 'Semua' ? 'Semua Bahagian' : d}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-[#999] mt-3" data-testid="result-count">
            {loading ? 'Memuatkan...' : `${filtered.length} perguruan dijumpai`}
          </p>
        </div>
      </section>

      {/* School list */}
      <section className="bg-[#F7F7F7] py-10 min-h-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white border border-[#E5E5E5] rounded h-28 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-[#999] text-sm">
              <Building2 size={40} className="mx-auto mb-4 text-[#E5E5E5]" />
              Tiada perguruan dijumpai.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((school) => (
                <div
                  key={school.id}
                  className="bg-white border border-[#E5E5E5] rounded p-6 hover:shadow-md hover:border-[#D71920]/30 transition-all"
                  data-testid={`school-card-${school.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-9 h-9 bg-[#D71920]/8 rounded flex items-center justify-center flex-shrink-0">
                      <Building2 size={16} className="text-[#D71920]" />
                    </div>
                    <span className="text-xs font-semibold text-[#16a34a] bg-[#16a34a]/10 px-2 py-0.5 rounded-full border border-[#16a34a]/20">
                      Aktif
                    </span>
                  </div>
                  <h3 className="font-bold text-[#0B0B0B] text-sm mb-2 leading-tight">{school.name}</h3>
                  <div className="flex items-center gap-1.5 text-xs text-[#777]">
                    <MapPin size={11} className="text-[#D71920] flex-shrink-0" />
                    <span>{school.district}, {school.division}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Notice */}
      <section className="bg-white py-10 border-t border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="p-5 bg-[#F7F7F7] border border-[#E5E5E5] rounded text-sm text-[#666] leading-relaxed">
            <strong>Nota:</strong> Direktori ini hanya memaparkan perguruan yang telah disahkan dan diluluskan oleh
            Sekretariat RSS Sarawak. Maklumat sensitif seperti nombor telefon dan alamat penuh tidak didedahkan kepada
            umum demi keselamatan dan privasi. Untuk pendaftaran perguruan baru, sila ke halaman{' '}
            <a href="/register-school" className="text-[#D71920] font-medium hover:underline">Daftar Perguruan</a>.
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
